import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Auth({ onAuthSuccess }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isRegister = location.pathname === '/register';
  const [activeTab, setActiveTab] = useState('customer');
  const [loading, setLoading] = useState(false);
  
  // Login method selection (passcode or password)
  const [customerLoginMethod, setCustomerLoginMethod] = useState('passcode'); // Default to passcode
  const [businessLoginMethod, setBusinessLoginMethod] = useState('passcode'); // Default to passcode
  
  // Passcode individual digits
  const [customerPasscodeDigits, setCustomerPasscodeDigits] = useState(['', '', '', '', '', '']);
  const [businessPasscodeDigits, setBusinessPasscodeDigits] = useState(['', '', '', '', '', '']);
  
  // Country codes for phone numbers
  const countryCodes = [
    { code: '+1', country: 'US', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+91', country: 'IN', flag: '🇮🇳' },
    { code: '+86', country: 'CN', flag: '🇨🇳' },
    { code: '+81', country: 'JP', flag: '🇯🇵' },
    { code: '+49', country: 'DE', flag: '🇩🇪' },
    { code: '+33', country: 'FR', flag: '🇫🇷' },
    { code: '+61', country: 'AU', flag: '🇦🇺' },
    { code: '+55', country: 'BR', flag: '🇧🇷' },
    { code: '+52', country: 'MX', flag: '🇲🇽' },
    { code: '+82', country: 'KR', flag: '🇰🇷' },
    { code: '+39', country: 'IT', flag: '🇮🇹' },
    { code: '+34', country: 'ES', flag: '🇪🇸' },
    { code: '+7', country: 'RU', flag: '🇷🇺' },
    { code: '+971', country: 'AE', flag: '🇦🇪' },
    { code: '+65', country: 'SG', flag: '🇸🇬' },
    { code: '+27', country: 'ZA', flag: '🇿🇦' },
    { code: '+20', country: 'EG', flag: '🇪🇬' },
    { code: '+234', country: 'NG', flag: '🇳🇬' },
  ];
  
  const [customerCountryCode, setCustomerCountryCode] = useState('+1');
  const [businessCountryCode, setBusinessCountryCode] = useState('+1');
  
  const [customerForm, setCustomerForm] = useState({
    email: '',
    password: '',
    full_name: '',
    phone_number: '',
    address: ''
  });

  const [businessForm, setBusinessForm] = useState({
    email: '',
    password: '',
    owner_name: '',
    business_name: '',
    business_type: '',
    business_description: '',
    address: '',
    phone_number: ''
  });

  const [adminForm, setAdminForm] = useState({
    email: '',
    password: ''
  });

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        // Trim all fields except password
        const trimmedForm = Object.keys(customerForm).reduce((acc, key) => {
          if (key === 'password') {
            acc[key] = customerForm[key]; // Keep password as-is
          } else {
            acc[key] = typeof customerForm[key] === 'string' ? customerForm[key].trim() : customerForm[key];
          }
          return acc;
        }, {});
        
        // Combine country code with phone number
        const formData = {
          ...trimmedForm,
          phone_number: `${customerCountryCode}${trimmedForm.phone_number}`
        };
        
        const response = await axios.post(`${API}/auth/customer/register`, formData);
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('role', 'customer');
        toast.success('Account created!');
        onAuthSuccess(response.data.user, 'customer');
        
        // Check for return URL
        const returnUrl = localStorage.getItem('returnUrl');
        if (returnUrl) {
          localStorage.removeItem('returnUrl');
          window.location.href = returnUrl;
        } else {
          window.location.href = '/customer/events';
        }
      } else {
        // Login - use passcode from 6-box input or password
        const loginCredential = customerLoginMethod === 'passcode' 
          ? customerPasscodeDigits.join('') 
          : customerForm.password;
        
        const response = await axios.post(`${API}/auth/login`, {
          email: customerForm.email.trim(),
          password: loginCredential,
          role: 'customer'
        });
        
        // Store authentication data
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('role', 'customer');
        
        toast.success('Login successful!');
        onAuthSuccess(response.data.user, 'customer');
        
        // Check for return URL
        const returnUrl = localStorage.getItem('returnUrl');
        if (returnUrl) {
          localStorage.removeItem('returnUrl');
          window.location.href = returnUrl;
        } else {
          window.location.href = '/customer/events';
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        // Trim all fields except password
        const trimmedForm = Object.keys(businessForm).reduce((acc, key) => {
          if (key === 'password') {
            acc[key] = businessForm[key]; // Keep password as-is
          } else {
            acc[key] = typeof businessForm[key] === 'string' ? businessForm[key].trim() : businessForm[key];
          }
          return acc;
        }, {});
        
        // Combine country code with phone number
        const formData = {
          ...trimmedForm,
          phone_number: trimmedForm.phone_number 
            ? `${businessCountryCode}${trimmedForm.phone_number}` 
            : undefined
        };
        
        const response = await axios.post(`${API}/auth/business/register`, formData);
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('role', 'business');
        toast.success('Account created!');
        onAuthSuccess(response.data.user, 'business');
        window.location.href = '/business/dashboard';
      } else {
        // Login - use passcode from 6-box input or password
        const loginCredential = businessLoginMethod === 'passcode' 
          ? businessPasscodeDigits.join('') 
          : businessForm.password;
        
        const response = await axios.post(`${API}/auth/login`, {
          email: businessForm.email.trim(),
          password: loginCredential,
          role: 'business'
        });
        
        // Store authentication data
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('role', 'business');
        
        toast.success('Login successful!');
        onAuthSuccess(response.data.user, 'business');
        window.location.href = '/business/dashboard';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Direct login without verification code - trim email only
      const response = await axios.post(`${API}/auth/login`, {
        email: adminForm.email.trim(),
        password: adminForm.password,
        role: 'admin'
      });
      
      // Store authentication data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('role', 'admin');
      
      toast.success('Login successful!');
      onAuthSuccess(response.data.user, 'admin');
      window.location.href = '/admin/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  // Passcode input handling for 6-box UI
  const handlePasscodeChange = (index, value, role) => {
    if (!/^\d?$/.test(value)) return; // Only allow single digits
    
    const digits = role === 'customer' ? [...customerPasscodeDigits] : [...businessPasscodeDigits];
    digits[index] = value;
    
    if (role === 'customer') {
      setCustomerPasscodeDigits(digits);
    } else {
      setBusinessPasscodeDigits(digits);
    }
    
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`${role}-passcode-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handlePasscodeKeyDown = (index, e, role) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      const digits = role === 'customer' ? [...customerPasscodeDigits] : [...businessPasscodeDigits];
      
      if (!digits[index] && index > 0) {
        // If current box is empty, move to previous and clear it
        const prevInput = document.getElementById(`${role}-passcode-${index - 1}`);
        if (prevInput) {
          prevInput.focus();
          digits[index - 1] = '';
          if (role === 'customer') {
            setCustomerPasscodeDigits(digits);
          } else {
            setBusinessPasscodeDigits(digits);
          }
        }
      } else {
        // Clear current box
        digits[index] = '';
        if (role === 'customer') {
          setCustomerPasscodeDigits(digits);
        } else {
          setBusinessPasscodeDigits(digits);
        }
      }
    }
  };

  const handlePasscodePaste = (e, role) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const digits = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
    
    if (role === 'customer') {
      setCustomerPasscodeDigits(digits);
    } else {
      setBusinessPasscodeDigits(digits);
    }
    
    // Focus the last filled box or first empty box
    const lastIndex = Math.min(pastedData.length, 5);
    const targetInput = document.getElementById(`${role}-passcode-${lastIndex}`);
    if (targetInput) targetInput.focus();
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-testid="auth-page">
      <Card className="w-full max-w-2xl p-8">
        <h2 className="font-playfair font-bold text-3xl text-center text-primary mb-6" data-testid="auth-title">
          {isRegister ? 'Create Account' : 'Login'}
        </h2>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="customer" data-testid="customer-tab">Customer</TabsTrigger>
            <TabsTrigger value="business" data-testid="business-tab">Business</TabsTrigger>
          </TabsList>

          <TabsContent value="customer">
            <form onSubmit={handleCustomerSubmit} className="space-y-4">
              {isRegister && (
                <>
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      value={customerForm.full_name}
                      onChange={(e) => setCustomerForm({ ...customerForm, full_name: e.target.value })}
                      required
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div>
                    <Label>Phone Number</Label>
                    <div className="flex gap-2">
                      <select
                        value={customerCountryCode}
                        onChange={(e) => setCustomerCountryCode(e.target.value)}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        data-testid="customer-country-code"
                      >
                        {countryCodes.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <Input
                        type="tel"
                        value={customerForm.phone_number}
                        onChange={(e) => setCustomerForm({ ...customerForm, phone_number: e.target.value.replace(/[^0-9]/g, '') })}
                        placeholder="1234567890"
                        required
                        data-testid="customer-phone-input"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </>
              )}
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={customerForm.email}
                  onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                  required
                  data-testid="customer-email-input"
                />
              </div>
              
              {!isRegister && (
                <div className="flex gap-2 justify-center mb-4">
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerLoginMethod('passcode');
                      setCustomerForm({ ...customerForm, password: '' });
                    }}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-full transition-all font-medium ${
                      customerLoginMethod === 'passcode'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-lg">🔑</span>
                    Passcode
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerLoginMethod('password');
                      setCustomerPasscodeDigits(['', '', '', '', '', '']);
                    }}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-full transition-all font-medium ${
                      customerLoginMethod === 'password'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-lg">🔒</span>
                    Password
                  </button>
                </div>
              )}
              
              {!isRegister && customerLoginMethod === 'passcode' ? (
                <div>
                  <Label className="text-center block mb-3">Enter 6-Digit Passcode</Label>
                  <div className="flex justify-center gap-2 mb-4">
                    {customerPasscodeDigits.map((digit, index) => (
                      <input
                        key={index}
                        id={`customer-passcode-${index}`}
                        type="password"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handlePasscodeChange(index, e.target.value, 'customer')}
                        onKeyDown={(e) => handlePasscodeKeyDown(index, e, 'customer')}
                        onPaste={(e) => handlePasscodePaste(e, 'customer')}
                        className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        required
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <Label>
                    {isRegister ? 'Password' : 'Password'}
                  </Label>
                  <Input
                    type="password"
                    value={customerForm.password}
                    onChange={(e) => setCustomerForm({ ...customerForm, password: e.target.value })}
                    required
                    data-testid="customer-password-input"
                  />
                </div>
              )}

              {!isRegister && (
                <div className="text-right -mt-2 mb-2">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-primary hover:underline font-outfit"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="customer-submit-button"
              >
                {loading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Login')}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="business">
            <form onSubmit={handleBusinessSubmit} className="space-y-4">
              {isRegister && (
                <>
                  <div>
                    <Label>Owner Name</Label>
                    <Input
                      value={businessForm.owner_name}
                      onChange={(e) => setBusinessForm({ ...businessForm, owner_name: e.target.value })}
                      required
                      data-testid="business-owner-input"
                    />
                  </div>
                  <div>
                    <Label>Business Name</Label>
                    <Input
                      value={businessForm.business_name}
                      onChange={(e) => setBusinessForm({ ...businessForm, business_name: e.target.value })}
                      required
                      data-testid="business-name-input"
                    />
                  </div>
                  <div>
                    <Label>Business Type</Label>
                    <Input
                      value={businessForm.business_type}
                      onChange={(e) => setBusinessForm({ ...businessForm, business_type: e.target.value })}
                      placeholder="e.g., Retail, Gold Shop, Mall"
                      required
                      data-testid="business-type-input"
                    />
                  </div>
                  <div>
                    <Label>Phone Number (Optional)</Label>
                    <div className="flex gap-2">
                      <select
                        value={businessCountryCode}
                        onChange={(e) => setBusinessCountryCode(e.target.value)}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        data-testid="business-country-code"
                      >
                        {countryCodes.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <Input
                        type="tel"
                        value={businessForm.phone_number}
                        onChange={(e) => setBusinessForm({ ...businessForm, phone_number: e.target.value.replace(/[^0-9]/g, '') })}
                        placeholder="1234567890"
                        data-testid="business-phone-input"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </>
              )}
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={businessForm.email}
                  onChange={(e) => setBusinessForm({ ...businessForm, email: e.target.value })}
                  required
                  data-testid="business-email-input"
                />
              </div>
              
              {!isRegister && (
                <div className="flex gap-2 justify-center mb-4">
                  <button
                    type="button"
                    onClick={() => {
                      setBusinessLoginMethod('passcode');
                      setBusinessForm({ ...businessForm, password: '' });
                    }}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-full transition-all font-medium ${
                      businessLoginMethod === 'passcode'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-lg">🔑</span>
                    Passcode
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBusinessLoginMethod('password');
                      setBusinessPasscodeDigits(['', '', '', '', '', '']);
                    }}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-full transition-all font-medium ${
                      businessLoginMethod === 'password'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-lg">🔒</span>
                    Password
                  </button>
                </div>
              )}
              
              {!isRegister && businessLoginMethod === 'passcode' ? (
                <div>
                  <Label className="text-center block mb-3">Enter 6-Digit Passcode</Label>
                  <div className="flex justify-center gap-2 mb-4">
                    {businessPasscodeDigits.map((digit, index) => (
                      <input
                        key={index}
                        id={`business-passcode-${index}`}
                        type="password"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handlePasscodeChange(index, e.target.value, 'business')}
                        onKeyDown={(e) => handlePasscodeKeyDown(index, e, 'business')}
                        onPaste={(e) => handlePasscodePaste(e, 'business')}
                        className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        required
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <Label>
                    {isRegister ? 'Password' : 'Password'}
                  </Label>
                  <Input
                    type="password"
                    value={businessForm.password}
                    onChange={(e) => setBusinessForm({ ...businessForm, password: e.target.value })}
                    required
                    data-testid="business-password-input"
                  />
                </div>
              )}

              {!isRegister && (
                <div className="text-right -mt-2 mb-2">
                  <button
                    type="button"
                    onClick={() => navigate('/password-reset')}
                    className="text-sm text-primary hover:underline font-outfit"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="business-submit-button"
              >
                {loading ? 'Please wait...' : (isRegister ? 'Create Business Account' : 'Login')}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <p className="text-center mt-6 text-sm text-gray-600">
          {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button
            onClick={() => navigate(isRegister ? '/login' : '/register')}
            className="text-primary font-semibold hover:underline"
            data-testid="toggle-auth-link"
          >
            {isRegister ? 'Login' : 'Sign up'}
          </button>
        </p>
      </Card>
    </div>
  );
}